import { useCallback, useContext } from 'react';
import { MyGroupsContext } from '../context/MyGroupsContext';
import { UserSchoolYear } from '../../../api/school-year';
import { Member, removeMemberFromGroup } from '../../../api/groups';

export type SchoolYear = {
  displayName: string;
  id: string;
} & UserSchoolYear;

export default function useGroups() {
  const {
    schoolYears,
    selectedSchoolYear,
    groupsWithSchool,
    groups,
    isLoadingGroups,
    onSchoolYearChange,
    onGroupsChange,
    fetchGroups,
  } = useContext(MyGroupsContext);

  const updateCounters = useCallback(
    (
      groupId: string,
      counters: {
        totalAccepted?: number;
        totalPending?: number;
        totalDeclined?: number;
      },
    ) => {
      onGroupsChange(grps =>
        grps?.map(g =>
          g.id === groupId
            ? {
                ...g,
                ...counters,
              }
            : g,
        ),
      );
    },
    [onGroupsChange],
  );

  const removeStudentFromGroup = useCallback(
    async (schoolYear: SchoolYear, groupId: string, member: Member) => {
      await removeMemberFromGroup(schoolYear.id, groupId, member.id);

      const group = groups ? groups.find(g => g.id === groupId) : undefined;

      if (group) {
        switch (member.status) {
          case 'accepted':
            updateCounters(groupId, { totalAccepted: group.totalAccepted - 1 });
            break;
          case 'pending':
            updateCounters(groupId, { totalPending: group.totalPending - 1 });
            break;
          case 'declined':
            updateCounters(groupId, { totalDeclined: group.totalDeclined - 1 });
            break;
          default:
            return;
        }
      }
    },
    [groups, updateCounters],
  );

  return {
    schoolYears,
    selectedSchoolYear,
    onSchoolYearChange,
    onGroupsChange,
    updateCounters,
    removeStudentFromGroup,
    groupsWithSchool,
    groups,
    isLoadingGroups,
    fetchGroups,
  };
}
